import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Breadcrumb, If } from '@/lib/v2/components';
import { CustomPaths } from '@/lib/v2/components/Breadcrumb/Breadcrumb';

import {
  atomDisableEditAction,
  atomGlobalLoadingAction,
  atomProcessingAction,
} from '@/src/modules/CampaignsModule/atoms/actionEmailAtom';
import {
  atomActionDetailQuickTools,
  atomHeaderCurrentStep,
} from '@/src/modules/CampaignsModule/atoms/actionsAtom';
import { atomActionInfoSms } from '@/src/modules/CampaignsModule/atoms/actionSMSAtom';
import { atomActionInfoWhatsApp } from '@/src/modules/CampaignsModule/atoms/actionWhatsAppAtom';
import { atomToggleEditActionsModal } from '@/src/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { ActionMenuTools } from '@/src/modules/CampaignsModule/components/ActionMenuTools';
import { WHATSAPP_PREFIX } from '@/src/modules/CampaignsModule/constants';
import { useActionData } from '@/src/modules/CampaignsModule/hooks/useActionData';
import { useCampaignLastPath } from '@/src/modules/CampaignsModule/hooks/useCampaignLastPath';
import {
  EChannelsID,
  ISmsInfoResponseV1,
} from '@/src/modules/CampaignsModule/interfaces/Campaigns';
import { IWhatsAppInfoResponseV1 } from '@/src/modules/CampaignsModule/interfaces/WhatsApp';
import { isActionEditable } from '@/src/modules/CampaignsModule/utils';
import { isActionFinish } from '@/src/modules/CampaignsModule/utils/isActionEditable';

import { BackToCampaignsModal } from './components/BackToCampaignsModal';
import { HeaderActionButtons } from './components/HeaderActionButtons';

import { atomHasInbox } from '@/modules/InboxModule/atoms/inboxAtom';

const HeaderSectionActions = () => {
  const { t } = useTranslation();
  const enableWhatsAppModule = useAtomValue(atomHasInbox);
  const navigate = useNavigate();
  const { actionId } = useParams();
  const { pathname } = useLocation();

  const [disableEditAction, setDisableEditAction] = useAtom(atomDisableEditAction);
  const [globalLoadingAction, setGlobalLoadingAction] = useAtom(atomGlobalLoadingAction);
  const processingAction = useAtomValue(atomProcessingAction);
  const currentStep = useAtomValue(atomHeaderCurrentStep);
  const setActionInfoSms = useSetAtom(atomActionInfoSms);
  const setActionDetailQuickTools = useSetAtom(atomActionDetailQuickTools);
  const setToggleEditActionsModal = useSetAtom(atomToggleEditActionsModal);

  const [toggleBackToCampaignsModal, setToggleBackToCampaignsModal] = useState<boolean>(false);
  const setActionInfoWhatsApp = useSetAtom(atomActionInfoWhatsApp);

  const { action } = useActionData(Number(actionId));
  const { campaignLastPath } = useCampaignLastPath();

  const isEditable = useMemo(() => isActionEditable(Number(action?.status)), [action?.status]);
  const isFinish = useMemo(() => isActionFinish(Number(action?.status)), [action?.status]);

  useEffect(() => {
    if (!action) return;

    const newDisabled = !isEditable;
    if (disableEditAction !== newDisabled) setDisableEditAction(newDisabled);

    if (globalLoadingAction) setGlobalLoadingAction(false);
  }, [
    action,
    disableEditAction,
    globalLoadingAction,
    isEditable,
    setDisableEditAction,
    setGlobalLoadingAction,
  ]);

  useEffect(() => {
    return () => setActionInfoSms({} as ISmsInfoResponseV1);
  }, [setActionInfoSms]);

  useEffect(() => {
    return () => setActionInfoWhatsApp({} as IWhatsAppInfoResponseV1);
  }, [setActionInfoWhatsApp]);

  const mainActionPath = useMemo(() => {
    const parts = pathname.replace(/^\/|\/$/g, '').split('/');
    const mainPath = parts.slice(3, 5).join('/');
    return mainPath;
  }, [pathname]);

  const handleBackHeader = useCallback(() => {
    if (pathname.includes('/scheduling') || pathname.includes('/summary')) {
      navigate(-1);
    } else {
      !isEditable ? navigate(campaignLastPath.action) : setToggleBackToCampaignsModal(true);
    }
  }, [campaignLastPath.action, isEditable, navigate, pathname]);

  const handleOpenModal = useCallback(() => {
    setActionDetailQuickTools({
      id: action?.actionId as number,
      name: action?.name as string,
      redirect: false,
      origin: 'detail',
      type: action?.channel as EChannelsID,
    });

    setToggleEditActionsModal(true);
  }, [setActionDetailQuickTools, action, setToggleEditActionsModal]);

  const handleClickItemDropdown = useCallback(() => {
    setActionDetailQuickTools({
      id: action?.actionId as number,
      name: action?.name as string,
      redirect: true,
      origin: 'detail',
      type: action?.channel as EChannelsID,
    });
  }, [action, setActionDetailQuickTools]);

  /* cspell:disable*/
  const HeaderSectionPaths = useMemo(() => {
    const getName = () => {
      if (!action) {
        return '';
      }
      if (enableWhatsAppModule) {
        return action.name.replace(WHATSAPP_PREFIX, '');
      }

      console.log({ action });

      return action.name;
    };

    let breadcrumbLinks: CustomPaths[] = [
      {
        id: 'campaign-id-path',
        name: action?.campaignName as string,
        to: {
          path: campaignLastPath.action,
        },
      },
      {
        id: 'action-name-path',
        name: getName(),
        to: {
          path: mainActionPath,
        },
      },
    ];

    if (pathname.includes('/scheduling') && !isFinish) {
      breadcrumbLinks = [
        ...breadcrumbLinks,
        {
          id: 'scheduling-path',
          name: t('BREADCRUMB.sendConfiguration'),
        },
      ];
    }

    if (pathname.includes('/summary') && !isFinish) {
      breadcrumbLinks = [
        ...breadcrumbLinks,
        {
          id: 'scheduling-path',
          name: t('BREADCRUMB.sendConfiguration'),
          to: {
            path: `${mainActionPath}/scheduling`,
          },
        },
        {
          id: 'summary-path',
          name: t('BREADCRUMB.summary'),
          to: {
            path: `${mainActionPath}/summary`,
          },
        },
      ];
    }

    return breadcrumbLinks;
  }, [
    action,
    campaignLastPath.action,
    mainActionPath,
    pathname,
    t,
    enableWhatsAppModule,
    isFinish,
  ]);
  /* cspell:enable*/

  const HeaderSectionActionButtons = useMemo(
    () => (
      <>
        <ActionMenuTools id="options-actions-dropdown" onClick={handleClickItemDropdown} />
        <HeaderActionButtons
          disabled={disableEditAction}
          loading={globalLoadingAction}
          processing={processingAction}
          step={currentStep}
        />
      </>
    ),
    [currentStep, disableEditAction, globalLoadingAction, handleClickItemDropdown, processingAction]
  );

  return (
    <div className="relative z-40">
      <If condition={!!action}>
        <Breadcrumb
          withoutMargin
          actionButtons={HeaderSectionActionButtons}
          customPaths={HeaderSectionPaths}
          handleOnBackClick={handleBackHeader}
          isEditable={!pathname.includes('scheduling') && !pathname.includes('summary')}
          itemId={action?.actionId}
          onEditableClick={handleOpenModal}
        />
      </If>

      <BackToCampaignsModal
        isOpen={toggleBackToCampaignsModal}
        onClose={setToggleBackToCampaignsModal}
      />
    </div>
  );
};

export default HeaderSectionActions;
